import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { useToast } from '../hooks/use-toast';
import { LogOut, Package, Mail, ShoppingCart, BarChart, Users, Tag, Star } from 'lucide-react';
import ProductsTab from '../components/admin/ProductsTab';
import ContactsTab from '../components/admin/ContactsTab';
import OrdersTab from '../components/admin/OrdersTab';
import StatsTab from '../components/admin/StatsTab';
import CustomersTab from '../components/admin/CustomersTab';
import PromoCodesTab from '../components/admin/PromoCodesTab';
import ReviewsTab from '../components/admin/ReviewsTab';

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();
  const { toast } = useToast();

  useEffect(() => {
    const token = localStorage.getItem('adminToken');
    if (!token) {
      navigate('/admin');
      return;
    }
    fetchStats();
  }, [navigate]);

  const fetchStats = async () => {
    try {
      const token = localStorage.getItem('adminToken');
      const response = await fetch('/api/admin/stats', {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      });

      if (!response.ok) {
        throw new Error('Unauthorized');
      }

      const data = await response.json();
      setStats(data);
    } catch (error) {
      toast({
        title: "Session expired",
        description: "Please login again.",
        variant: "destructive",
      });
      localStorage.removeItem('adminToken');
      navigate('/admin');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('adminToken');
    toast({
      title: "Logged out",
      description: "You have been logged out successfully.",
    });
    navigate('/admin');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center" style={{ backgroundColor: '#f7f5f2' }}>
        <p className="text-xl" style={{ color: '#736c64' }}>Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen" style={{ backgroundColor: '#f7f5f2' }}>
      {/* Header */}
      <div className="border-b" style={{ backgroundColor: '#4a2c1a', borderColor: '#6b4423' }}>
        <div className="container mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4">
              <img 
                src="https://customer-assets.emergentagent.com/job_48fa2328-69a6-47d6-a09d-67288600a773/artifacts/di13voej_A14597EA-17F5-436E-B720-266D21072789.PNG" 
                alt="The Maple Daddies" 
                className="h-12 w-auto"
              />
              <div>
                <h1 className="text-2xl font-bold" style={{ color: '#FF8C19' }}>
                  Admin Panel
                </h1>
                <p className="text-sm" style={{ color: '#bbb5ae' }}>Manage your maple syrup business</p>
              </div>
            </div>
            <div className="flex items-center space-x-4">
              <a href="/" target="_blank" rel="noopener noreferrer">
                <Button variant="outline" className="border-2" style={{ borderColor: '#FF8C19', color: '#FF8C19' }}>
                  View Website
                </Button>
              </a>
              <Button
                onClick={handleLogout}
                variant="outline"
                className="border-2"
                style={{ borderColor: '#dc2626', color: '#dc2626' }}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Logout
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-4 py-8">
        <Tabs defaultValue="stats" className="w-full">
          <TabsList className="grid w-full grid-cols-7 mb-8">
            <TabsTrigger value="stats" className="flex items-center space-x-2">
              <BarChart className="h-4 w-4" />
              <span>Dashboard</span>
            </TabsTrigger>
            <TabsTrigger value="products" className="flex items-center space-x-2">
              <Package className="h-4 w-4" />
              <span>Products</span>
            </TabsTrigger>
            <TabsTrigger value="orders" className="flex items-center space-x-2">
              <ShoppingCart className="h-4 w-4" />
              <span>Orders</span>
            </TabsTrigger>
            <TabsTrigger value="promos" className="flex items-center space-x-2">
              <Tag className="h-4 w-4" />
              <span>Promo Codes</span>
            </TabsTrigger>
            <TabsTrigger value="customers" className="flex items-center space-x-2">
              <Users className="h-4 w-4" />
              <span>Customers</span>
            </TabsTrigger>
            <TabsTrigger value="reviews" className="flex items-center space-x-2">
              <Star className="h-4 w-4" />
              <span>Reviews</span>
            </TabsTrigger>
            <TabsTrigger value="contacts" className="flex items-center space-x-2">
              <Mail className="h-4 w-4" />
              <span>Messages</span>
            </TabsTrigger>
          </TabsList>

          <TabsContent value="stats">
            <StatsTab stats={stats} onRefresh={fetchStats} />
          </TabsContent>

          <TabsContent value="products">
            <ProductsTab />
          </TabsContent>

          <TabsContent value="orders">
            <OrdersTab />
          </TabsContent>

          <TabsContent value="promos">
            <PromoCodesTab />
          </TabsContent>

          <TabsContent value="customers">
            <CustomersTab />
          </TabsContent>

          <TabsContent value="reviews">
            <ReviewsTab />
          </TabsContent>

          <TabsContent value="contacts">
            <ContactsTab />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

export default AdminDashboard;
